import { Map } from 'immutable';

import { formatDate, getActualDate, getDateKey, getDayStart, getDayEnd, isAfter, setDay, setSeconds, setTime } from '../time';
import { getSize } from '../iterable/getSize';

import { DAYS_SPAN, SCHEDULE_LIMIT, TABLE_ROW } from '../../constants/schedule';
import { EMPTY, SEPARATOR } from '../../constants';

export function getToday() {
  return getDayStart(getActualDate());
}

export function getTimeRangeEnd(start) {
  return getDayEnd(setDay(start, start.getDate() + DAYS_SPAN - 1));
}

export function getTimeRangeStart(today, days) {
  return setDay(today, today.getDate() + Math.floor(days / DAYS_SPAN) * DAYS_SPAN);
}

export function shapeDate({ date, time, type }) {
  switch (type) {
    case TABLE_ROW.DATE: {
      const locale = 'en-US';
      const options = { weekday: 'long', month: 'long', day: 'numeric' };
      return formatDate({ locale, options, date });
    }

    default: {
      const locale = 'en-US';
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: time ? 'numeric' : undefined,
        minute: time ? 'numeric' : undefined,
      };

      return formatDate({ locale, options, date }).replace(SEPARATOR.COMMA, '');
    }
  }
}

export function getScheduleDatesRange() {
  const min = setSeconds(getActualDate(), 0);
  const max = setTime(setDay(min, min.getDate() + SCHEDULE_LIMIT), 23, 59);
  return { min, max };
}

export function patchTimeRanges(state) {
  const today = getToday();

  function patchRanges(updates) {
    function updateRanges(ranges = EMPTY.MAP) {
      function checkNextRange(result, rangeKey, rangeIndex) {
        if (rangeIndex >= getSize(result.get('keys'))) return result;

        const timestamp = result.getIn(['timestamps', rangeKey]);
        const nextRange = getDayStart(setDay(timestamp, timestamp.getDate() + DAYS_SPAN));
        const nextRangeKey = getDateKey(nextRange);

        function updateRangeKeys(keys = EMPTY.LIST) {
          return keys.splice(rangeIndex, 0, nextRangeKey);
        }

        function updateRangeTimestamps(timestamps = EMPTY.MAP) {
          return timestamps.set(nextRangeKey, nextRange);
        }

        if (!result.hasIn(['timestamps', nextRangeKey])) {
          return checkNextRange(
            result.update('keys', updateRangeKeys).update('timestamps', updateRangeTimestamps),
            nextRangeKey,
            rangeIndex + 1,
          );
        }

        return checkNextRange(result, nextRangeKey, rangeIndex + 1);
      }

      function addCurrentRange(result = EMPTY.MAP) {
        const currentRangeKey = getDateKey(today);

        function updateRangeKeys(keys = EMPTY.LIST) {
          return keys.push(currentRangeKey);
        }

        function updateRangeTimestamps(timestamps = EMPTY.MAP) {
          return timestamps.set(currentRangeKey, today);
        }

        return result.update('keys', updateRangeKeys).update('timestamps', updateRangeTimestamps);
      }

      return getSize(ranges.get('keys')) > 1
        ? checkNextRange(ranges, ranges.get('keys').first(), 1)
        : getSize(ranges.get('keys')) === 0
          ? addCurrentRange(ranges)
          : ranges;
    };

    return updates.update('ranges', updateRanges);
  }

  function patchTime(updates) {
    function updateTime(time = EMPTY.MAP) {
      let start = time.get('start');

      if (!start) {
        const firstRange = updates.getIn(['ranges', 'timestamps', updates.getIn(['ranges', 'keys', 0])]);

        if (firstRange) {
          start = new Date(firstRange);
        } else {
          start = today;
        }
      }

      if (isAfter(today, start)) {
        start = today;
      }

      if (getDateKey(today) !== getDateKey(time.get('today'))) {
        return getDateKey(start) === getDateKey(time.get('start'))
          ? time.set('today', today)
          : Map({ today, start, end: getTimeRangeEnd(start) });
      }

      if (getDateKey(start) !== getDateKey(time.get('start'))) {
        return Map({ today, start, end: getTimeRangeEnd(start) });
      }

      return time;
    }

    return updates.update('time', updateTime);
  }

  return patchTime(patchRanges(state));
}
